<template>
  <div v-loading="loading" class="ca-order-detail">
    <template v-if="detail">
      <!-- 头部：订单号 + 右侧操作按钮 -->
      <div class="ca-order-detail__header">
        <h2 class="ca-order-detail__title">
          订单号：<b>{{ detail.orderCode }}</b>
        </h2>
        <div class="ca-order-detail__actions">
          <el-button
            v-if="showPayNow"
            class="ca-btn--primary"
            :loading="payLoading"
            @click="handlePayNow"
          >
            立即支付
          </el-button>
          <el-button v-if="showWireInfo" class="ca-btn--primary" @click="goWireTransfer">
            查看电汇信息
          </el-button>
          <el-button
            v-if="showAuth && !showCfcaRenew"
            class="ca-btn--primary"
            :loading="authLoading"
            @click="handleAuth"
          >
            信息认证
          </el-button>
          <!-- CA在线续费：与列表页同口径（CFCA 续费单在 30 待认证 / 40 认证通过 / 80 已驳回展示），
               替代「信息认证」按钮，同跳续签页 -->
          <el-button v-if="showCfcaRenew" class="ca-btn--primary" @click="goRenew">
            CA在线续费
          </el-button>
        </div>
      </div>

      <!-- 订单状态（认证驳回 80 按「待认证」展示，见 util/caForm.js orderStatusText） -->
      <p class="ca-order-detail__status">
        订单状态：<b>{{ statusText }}</b>
      </p>

      <!-- 步骤条：下单 → 支付 → 完成，当前阶段由订单状态 + 支付方式 + 是否已传底单推导 -->
      <div class="ca-order-detail__steps">
        <el-steps :active="stageIndex" align-center finish-status="success">
          <el-step v-for="s in ORDER_STAGE_STEPS" :key="s.key" :title="s.label" />
        </el-steps>
      </div>

      <!-- 订单摘要：自绘带边框表格（element-ui 2.x 没有 el-descriptions，勿用）；
           标签照原型：订单日期/订单号/使用系统/业务类型/办理状态/是否加急 -->
      <div class="ca-order-detail__summary">
        <div class="ca-order-detail__summary-row">
          <span class="ca-order-detail__summary-th">订单日期</span>
          <span class="ca-order-detail__summary-td">{{ detail.orderTime }}</span>
          <span class="ca-order-detail__summary-th">订单号</span>
          <span class="ca-order-detail__summary-td">{{ detail.orderCode }}</span>
          <span class="ca-order-detail__summary-th">使用系统</span>
          <span class="ca-order-detail__summary-td">{{ detail.platformName || '--' }}</span>
        </div>
        <div class="ca-order-detail__summary-row">
          <span class="ca-order-detail__summary-th">业务类型</span>
          <span class="ca-order-detail__summary-td">{{ detail.bizTypeName || '--' }}</span>
          <span class="ca-order-detail__summary-th">申请数量</span>
          <span class="ca-order-detail__summary-td">{{ detail.numberVal || '--' }}</span>
          <span class="ca-order-detail__summary-th">申请年限</span>
          <span class="ca-order-detail__summary-td">{{ detail.limitName || '--' }}</span>
        </div>
        <div class="ca-order-detail__summary-row">
          <span class="ca-order-detail__summary-th">办理状态</span>
          <span class="ca-order-detail__summary-td">{{ statusText }}</span>
          <span class="ca-order-detail__summary-th">是否加急</span>
          <!-- isUrgent 是字符串 '1'/'0'，不能用真值判断（'0' 也是真值） -->
          <span class="ca-order-detail__summary-td">{{
            String(detail.isUrgent) === '1' ? '是' : '否'
          }}</span>
          <span class="ca-order-detail__summary-th" />
          <span class="ca-order-detail__summary-td" />
        </div>
      </div>
      <div class="ca-order-detail__total">
        商品总价：<b>¥ {{ detail.totalAmount }}</b>
      </div>

      <!-- 信息区块（字段集见下方 DETAIL_FIELDS 常量，纯文本；附件走 FilePreview） -->
      <div v-for="sec in [6, 7]" :key="sec" class="ca-order-detail__block">
        <div class="ca-order-detail__block-title">
          <span>{{ sectionTitles[sec] }}</span>
        </div>
        <div class="ca-order-detail__fields">
          <div
            v-for="field in fieldsBySection(sec)"
            :key="field.key"
            class="ca-order-detail__field"
            :class="{ 'ca-order-detail__field--full': field.fullRow || field.type }"
          >
            <span class="ca-label">{{ field.label }}</span>
            <file-preview
              v-if="field.type === 'image' || field.type === 'file'"
              :file-id="detail[field.key]"
            />
            <span v-else class="ca-value">{{ detail[field.key] || '--' }}</span>
          </div>
        </div>
      </div>
    </template>

    <auth-intro-dialog :visible.sync="authIntroVisible" />
    <!-- 上海CA续签：CA 信息获取读证书 → 确定后调 shcaAuthRenew（caSerialNo 必传） -->
    <ca-binding-dialog ref="caBindingDialog" @success="handleShecaAuthSuccess" />
  </div>
</template>

<script>
import FilePreview from '../components/FilePreview.vue'
import AuthIntroDialog from '../components/AuthIntroDialog.vue'
import CaBindingDialog from '../components/CaBindingDialog.vue'
import { getOrderDetail, paySign, shcaAuthApply } from '@/api/ca'
import { CA_TYPE, orderStatusText } from '../util/caForm'
import { payCaOrder, markPayInitiated } from '../util/pay'

/** 订单阶段步骤条（下单 → 支付 → 完成），阶段由订单状态码推导（见 stageIndex） */
const ORDER_STAGE_STEPS = [
  { key: 'CREATE', label: '下单' },
  { key: 'PAY', label: '支付' },
  { key: 'DONE', label: '完成' },
]

/**
 * 详情页只读字段集合（本地定义，key / 标签 / 顺序与申请页一致）：
 * type 仅区分附件（image / file 走 FilePreview）与文本；
 * fullRow 的长值字段（地址类）与所有附件字段（文件名可能较长，半行放不下会溢出）占整行；
 * section 的 6 / 7 对应 SECTION_TITLES 的区块。
 */
const CFCA_DETAIL_FIELDS = [
  { key: 'linkMan', label: '被授权经办人姓名', section: 6 },
  { key: 'linkTel', label: '手机号', section: 6 },
  { key: 'identityCode', label: '身份证号', section: 6 },
  { key: 'linkEmail', label: '邮箱', section: 6 },
  { key: 'postAddr', label: 'CA邮寄地址', section: 6, fullRow: true },
  { key: 'applyOrgName', label: '单位名称', section: 7 },
  { key: 'licenceCode', label: '统一社会信用代码', section: 7 },
  { key: 'legalName', label: '法定代表人姓名', section: 7 },
  { key: 'legalIdNo', label: '法定代表人身份证号', section: 7 },
  { key: 'licenceCodeFile', label: '营业执照扫描件', type: 'image', section: 7 },
  { key: 'authFileId', label: 'CA申请授权书', type: 'file', section: 7 },
]

/**
 * 上海CA 条件字段的业务类型白名单（后端固定的 productValue 数字，与申请/编辑页同口径；
 * 含义见 util/caForm.js 的 BIZ_TYPE_OPTIONS，后端新增业务类型时需同步登记）：
 *  - LEGAL_FIELD_BIZ_TYPES：法定代表人身份证号 + 身份证扫描件
 *    （法定代表人姓名已全业务放开，恒显不在此列）
 *    → 1 申请单位证书+法人证书 / 2 续费单位证书+法人证书 / 5 申请法人证书 / 6 续费法人证书
 *  - LICENCE_FILE_BIZ_TYPES：营业执照扫描件 → 3 申请单位证书 / 4 续费单位证书
 *  - ORG_SEAL_BIZ_TYPES：单位公章图片 → 1 申请单位证书+法人证书 / 3 申请单位证书 / 7 申请单位证书(境外)
 *  - LEGAL_SEAL_BIZ_TYPES：法人私章/手写签字图片 → 1 申请单位证书+法人证书 / 5 申请法人证书
 */
const LEGAL_FIELD_BIZ_TYPES = [1, 2, 5, 6]
const LICENCE_FILE_BIZ_TYPES = [3, 4]
const ORG_SEAL_BIZ_TYPES = [1, 3, 7]
const LEGAL_SEAL_BIZ_TYPES = [1, 5]

const SHCA_DETAIL_FIELDS = [
  { key: 'linkMan', label: '经办人姓名', section: 6 },
  { key: 'identityCode', label: '经办人身份证号', section: 6 },
  { key: 'linkTel', label: '经办人手机号', section: 6 },
  // 经办人身份证扫描件：固定字段（非条件字段），全业务类型恒显（与申请页同口径）
  { key: 'identityFileId', label: '经办人身份证扫描件', type: 'image', section: 6 },
  { key: 'linkEmail', label: '经办人邮箱', section: 6 },
  // legalName 已全业务放开（恒显）；其下为条件字段（bizTypes 白名单命中才显示，
  // 与申请页 showLegalFields / showOrgSealFile / showLegalSealFile 同口径）
  { key: 'legalName', label: '法定代表人姓名', section: 6 },
  { key: 'legalIdNo', label: '法定代表人身份证号', section: 6, bizTypes: LEGAL_FIELD_BIZ_TYPES },
  {
    key: 'legalIdFileId',
    label: '法定代表人身份证扫描件',
    type: 'image',
    section: 6,
    bizTypes: LEGAL_FIELD_BIZ_TYPES,
  },
  { key: 'applyOrgName', label: '单位名称', section: 6 },
  { key: 'licenceCode', label: '统一社会信用代码', section: 6 },
  {
    key: 'licenceCodeFile',
    label: '营业执照扫描件',
    type: 'image',
    section: 6,
    bizTypes: LICENCE_FILE_BIZ_TYPES,
  },
  {
    key: 'orgSealFileId',
    label: '单位公章图片',
    type: 'image',
    section: 6,
    bizTypes: ORG_SEAL_BIZ_TYPES,
  },
  {
    key: 'legalSealFileId',
    label: '法人私章/手写签字图片',
    type: 'image',
    section: 6,
    bizTypes: LEGAL_SEAL_BIZ_TYPES,
  },
  { key: 'receiverName', label: '收件人姓名', section: 7 },
  { key: 'receiverMobile', label: '收件人手机号', section: 7 },
  { key: 'postAddr', label: '收件地址', section: 7, fullRow: true },
]

/** 信息区块标题（6 / 7 是字段定义里 section 的稳定逻辑标识） */
const SECTION_TITLES = {
  [CA_TYPE.CFCA]: { 6: '被授权人信息', 7: '申请单位信息' },
  [CA_TYPE.SHCA]: { 6: '经办人/申请单位信息', 7: '收件信息' },
}

export default {
  name: 'CaOrderDetail',
  components: { FilePreview, AuthIntroDialog, CaBindingDialog },
  data() {
    return {
      ORDER_STAGE_STEPS,
      detail: null, // 订单详情（接口返回原样使用，orderStatus 为数字码库值）
      loading: false, // 详情加载中
      payLoading: false, // 「立即支付」调 signOrder 中 → 按钮 loading 防重复
      authLoading: false, // 「信息认证」调 sheca 认证下单中 → 按钮 loading 防重复
      authIntroVisible: false, // CFCA 申请单「信息认证说明」弹框开关
    }
  },
  computed: {
    // 订单 id 从 query 取，按字符串原样透传给接口，不做 Number() 转换
    orderId() {
      return this.$route.query.orderId
    },
    // 当前 CA 类型枚举：caTypeName 仅 CFCA / 上海CA 两种，优先按 CFCA 判断；
    // 决定渲染「被授权人信息」（CFCA）还是「经办人/收件信息」（上海CA）
    caType() {
      return this.detail && this.detail.caTypeName === 'CFCA' ? CA_TYPE.CFCA : CA_TYPE.SHCA
    },
    // 业务类型 value：详情接口直接下发 productValue（字符串形态，如 '4'；含义见
    // util/caForm.js 的 BIZ_TYPE_OPTIONS），转数字后作为上海CA 条件字段显隐的判定依据
    productValue() {
      const v = this.detail && this.detail.productValue
      return v == null || v === '' ? null : Number(v)
    },
    // 是否续费单：取详情接口下发的 caFunctionType（0 申请 / 1 续费，与列表页同口径）
    isRenew() {
      return String(this.detail && this.detail.caFunctionType) === '1'
    },
    // 详情页字段集：按 caType 取本地字段表，再按业务类型 value 白名单过滤上海CA 条件字段
    // （法定代表人三件套 / 营业执照 / 单位公章 / 法人私章，与申请页显隐同口径；
    // 未登记的新业务类型不显示条件字段）
    fields() {
      const all = this.caType === CA_TYPE.SHCA ? SHCA_DETAIL_FIELDS : CFCA_DETAIL_FIELDS
      return all.filter((f) => !f.bizTypes || f.bizTypes.includes(this.productValue))
    },
    sectionTitles() {
      return SECTION_TITLES[this.caType]
    },
    // 状态码（字符串化，库值可能是 number 形态）
    statusCode() {
      return String((this.detail && this.detail.orderStatus) || '')
    },
    // 状态文案：认证驳回(80) 按「待认证」展示（口径见 util/caForm.js orderStatusText）
    statusText() {
      return orderStatusText(this.detail)
    },
    /**
     * 步骤条高亮下标（阶段规则按数字码，需求 5.10「订单阶段」）：
     * 10 待付款 → 0 下单；50 已寄出 / 60 已完成 → 3（越过末位，三个节点全部置绿）；
     * 其余（含 15 待电汇确认）→ 1 支付。
     * 注意 el-steps 语义：active 是「进行中」节点的下标，active=2 时第 3 节点是 process（黑色），
     * 只有 active=3 三个节点才全部落入 finish（绿色）——已寄出即视为流程走完，故返回 3。
     * （15 已含「电汇底单已上传」语义，无需再看凭证字段）
     */
    stageIndex() {
      if (['50', '60'].includes(this.statusCode)) return 3
      if (['90'].includes(this.statusCode)) return -1
      if (this.statusCode === '10') return 1
      return 2
    },
    /**
     * 详情页右上角操作按钮（与列表卡的按钮矩阵是两套口径，产品明确不要强求一致）：
     * 只有「待付款(10)·网上支付 → 立即支付」「待付款(10)·电汇 → 查看电汇信息」
     * 「待认证(30) → 信息认证」「CFCA 续费单 30/40/80 → CA在线续费」，其余状态一律不展示操作按钮。
     */
    showPayNow() {
      return this.statusCode === '10' && this.detail?.payType === '20'
    },
    showWireInfo() {
      return this.statusCode === '10' && this.detail?.payType === '10'
    },
    showAuth() {
      return this.statusCode === '30'
    },
    // CA在线续费入口：与列表页 canCfcaRenew 同口径（CFCA 续费单 + 30 待认证 / 40 认证通过 / 80 已驳回）；
    // 命中时替代「信息认证」按钮（30 状态下两者同跳续签页，不重复展示）
    showCfcaRenew() {
      return (
        this.caType === CA_TYPE.CFCA && this.isRenew && ['30', '40', '80'].includes(this.statusCode)
      )
    },
  },
  created() {
    // orderId 缺失（手拼 URL / 历史链接）→ 不发详情请求，提示后回订单列表
    if (!this.orderId) {
      this.$message.warning('订单参数缺失，已返回订单列表')
      this.$router.replace({ name: 'ca-orders' })
      return
    }
    this.loadDetail()
  },
  methods: {
    loadDetail() {
      this.loading = true
      getOrderDetail({ orderId: this.orderId })
        .then((res) => {
          if (!res.data) {
            this.$message.error('订单不存在或已删除')
            this.$router.replace({ name: 'ca-orders' })
            return
          }
          this.detail = res.data
        })
        .finally(() => {
          this.loading = false
        })
    },
    fieldsBySection(sec) {
      return this.fields.filter((f) => f.section === sec)
    },
    formatSummaryDate(createTime) {
      // 摘要表日期格式：2026.08.30（点分隔，与列表卡头行的「2026年08月30日」不同）
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(createTime || '')
      return m ? `${m[1]}.${m[2]}.${m[3]}` : createTime || '--'
    },
    goWireTransfer() {
      this.$router.push({
        name: 'ca-wire-transfer',
        query: { orderId: this.orderId },
      })
    },
    // 【CA在线续费】CFCA 续费单进在线续签页（ukey 插件续签）
    goRenew() {
      this.$router.push({ name: 'ca-order-renew', query: { orderId: this.orderId } })
    },
    handlePayNow() {
      this.payLoading = true
      paySign({ orderId: this.orderId })
        .then((res) => {
          // 收银台打开后打「已发起支付」标记，回到订单列表页时凭标记弹支付结果确认
          if (payCaOrder(res.data)) markPayInitiated(this.orderId)
        })
        .finally(() => {
          this.payLoading = false
        })
    },
    // 信息认证分流：与订单列表页同一套规则（CFCA 续费 → 续签页；CFCA 申请 → 说明弹框；
    // 上海CA 续期 → CA 信息认证弹框（读证书序列号后调 shcaAuthRenew）；上海CA 申请 → 直接换认证 URL）。
    // 续费判定走 isRenew（详情接口 caFunctionType，与列表页同口径）
    handleAuth() {
      if (this.caType === CA_TYPE.CFCA) {
        if (this.isRenew) {
          this.$router.push({
            name: 'ca-order-renew',
            query: { orderId: this.orderId },
          })
        } else {
          this.authIntroVisible = true
        }
        return
      }
      if (this.isRenew) {
        this.$refs.caBindingDialog.open(this.orderId)
        return
      }
      // 上海CA申请：frontRedirectUrl 传当前页全路径，认证完成后由认证 H5 跳转带回
      this.authLoading = true
      shcaAuthApply({ orderId: this.orderId, frontRedirectUrl: window.location.href })
        .then((res) => this.handleShecaAuthSuccess(res.data && res.data.authUrl))
        .catch(() => {
          // 业务错误提示由 http 拦截器统一 toast，这里仅兜住 rejection
        })
        .finally(() => {
          this.authLoading = false
        })
    },
    // 打开上海CA认证 H5（申请直接返回 / 续签弹框确认成功后返回共用）
    handleShecaAuthSuccess(url) {
      // authUrl 缺失不是接口报错，拦截器不会提示，这里单独兜底
      if (!url) {
        this.$message.error('操作失败，请重试')
        return
      }
      window.open(url)
    },
  },
}
</script>
